import { useEffect, useRef, type KeyboardEvent } from 'react';
import { t } from '../services/language-manager';

interface SessionExpiredDialogProps {
  onSignIn: () => void;
  busy: boolean;
  /** Why the last Sign in again did not finish, already translated. */
  error?: string | null;
}

/**
 * Shown over any page once the Genesys session has expired. There is no
 * Cancel: every backend call would fail until the admin signs in again.
 * Sign in again keeps the page's unsaved edits and returns to the same page.
 */
export function SessionExpiredDialog({ onSignIn, busy, error }: SessionExpiredDialogProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    buttonRef.current?.focus();
  }, []);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Tab') {
      // One-button focus trap.
      e.preventDefault();
      buttonRef.current?.focus();
    }
  };

  return (
    <div className="confirm-dialog__backdrop">
      <div
        className="confirm-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="session-expired-title"
        aria-describedby="session-expired-message"
        onKeyDown={handleKeyDown}
      >
        <h3 id="session-expired-title" className="confirm-dialog__title">{t('session-expired-title')}</h3>
        <p id="session-expired-message" className="confirm-dialog__message">{t('session-expired-msg')}</p>
        {error && <p className="confirm-dialog__error" role="alert">{error}</p>}
        <div className="confirm-dialog__actions">
          <button
            ref={buttonRef}
            type="button"
            className="btn btn--primary"
            onClick={onSignIn}
            disabled={busy}
          >
            {t('session-expired-button')}
          </button>
        </div>
      </div>
    </div>
  );
}
